/**
 * STATE_REGISTRY: the single source the router, header, footer, sitemap and
 * Texas-only feature gates all read. Data lives in stateRegistry.data.json so the
 * Node sitemap script and the app share one file.
 *
 * Route resolution is per state per TRACK (blueprint 1.1):
 *   active       -> full experience
 *   coming_soon  -> waitlist page for that track (no plan data, no quote intake)
 *   disabled / track absent / slug not in registry -> 404
 *
 * This is a convenience only. The API is the enforcement point.
 */
import registryData from "./stateRegistry.data.json";

export const TRACKS = ["residential", "commercial"];

// Code-level invariants (blueprint 1.1). A data-entry mistake in the JSON must not
// be able to make NY routable.
export const RESIDENTIAL_FORBIDDEN_STATES = ["NY"];
export const UNVERIFIED_COMMERCIAL_STATES = ["NY", "NJ", "MD"];

// Top-level national paths a state slug must never collide with.
export const RESERVED_SLUGS = ["admin", "market-pulse", "how-it-works", "data-centers", "account", "bill-check", "api", "help", "about", "newsletter"];

// Which route segment belongs to which track. Segments not listed are "shared".
// `solar` is mapped to the residential track (2026-09-30 reduced-scope launch, Reba-CPO/
// Witherspoon-CLO): RC has zero signed solar-buyback partnerships, so Solar must go
// coming_soon in lockstep with Plans/Providers rather than staying a standalone always-on
// feature route -- reusing this same track-status mechanism means it resolves straight to
// the existing WaitlistPage with no new component. Mirror any change here in
// scripts/generate-seo.js's TRACK_SEGMENTS/FEATURE_SEGMENTS.
export const ROUTE_TRACK = {
  plans: "residential",
  providers: "residential",
  enroll: "residential",
  commercial: "commercial",
  solar: "residential",
};

// Frontend feature flag -> the sub-route it powers (used by the router, nav and tests).
// solar_content intentionally absent: solar now resolves via ROUTE_TRACK above, not as a
// standalone feature route (see comment there).
export const FEATURE_ROUTES = {
  esid_lookup: "esid-lookup",
  tdu_content: "utilities",
};

export function validateRegistry(rows) {
  const slugs = new Set();
  const codes = new Set();
  rows.forEach((s) => {
    if (slugs.has(s.slug) || codes.has(s.code)) throw new Error(`Duplicate registry row: ${s.slug}`);
    slugs.add(s.slug);
    codes.add(s.code);
    if (s.slug !== s.slug.toLowerCase()) throw new Error(`Slug must be lowercase: ${s.slug}`);
    if (RESERVED_SLUGS.includes(s.slug)) throw new Error(`Slug collides with a reserved path: ${s.slug}`);
    if (RESIDENTIAL_FORBIDDEN_STATES.includes(s.code) && s.tracks.residential) {
      throw new Error(`${s.code} may never have a residential track`);
    }
    const commercial = s.tracks.commercial;
    if (
      UNVERIFIED_COMMERCIAL_STATES.includes(s.code) &&
      commercial &&
      commercial.launch_status !== "disabled" &&
      !commercial.verification_ref
    ) {
      throw new Error(`${s.code} commercial track requires verification_ref`);
    }
  });
  return rows;
}

const rows = validateRegistry(registryData.states);

export const STATE_REGISTRY = Object.freeze(
  rows.reduce((acc, s) => {
    acc[s.slug] = Object.freeze(s);
    return acc;
  }, {})
);

export function getState(slug) {
  return Object.prototype.hasOwnProperty.call(STATE_REGISTRY, slug) ? STATE_REGISTRY[slug] : null;
}

export function getStateByCode(code) {
  return Object.values(STATE_REGISTRY).find((s) => s.code === String(code || "").toUpperCase()) || null;
}

export function listStates() {
  return Object.values(STATE_REGISTRY).sort((a, b) => a.launch_order - b.launch_order);
}

/** 'active' | 'coming_soon' | 'disabled' (absent track or unknown state = 'disabled') */
export function trackStatus(state, track) {
  if (!state || !state.tracks[track]) return "disabled";
  return state.tracks[track].launch_status;
}

export function isTrackActive(state, track) {
  return trackStatus(state, track) === "active";
}

/** A state-only UI feature is on only when the state lists it AND has a track that isn't disabled. */
export function hasFeature(state, feature) {
  if (!state) return false;
  if (!TRACKS.some((t) => trackStatus(state, t) !== "disabled")) return false;
  return state.frontend_features.includes(feature);
}

/**
 * Resolve a (state, route segment) pair to what the router should do.
 * Returns { kind: 'page' | 'waitlist' | 'not_found', track? }.
 * `segment` is the first path segment after the state slug ('' for the landing).
 */
export function resolveStateRoute(slug, segment = "") {
  const state = getState(slug);
  if (!state) return { kind: "not_found" };

  const track = ROUTE_TRACK[segment];
  if (track) {
    const status = trackStatus(state, track);
    if (status === "active") return { kind: "page", track };
    if (status === "coming_soon") return { kind: "waitlist", track };
    return { kind: "not_found" };
  }

  if (segment === "") return { kind: "page" }; // landing renders per-track status itself

  const feature = Object.keys(FEATURE_ROUTES).find((f) => FEATURE_ROUTES[f] === segment);
  if (feature) return hasFeature(state, feature) ? { kind: "page" } : { kind: "not_found" };

  return { kind: "not_found" };
}

export const statePath = (slug, sub = "") => `/${slug}${sub ? `/${sub}` : ""}`;
